/**
 * Tri-state schedule intent for multi-country bulk edit.
 */

import { CalendarClock, CalendarOff, CalendarX, type LucideIcon } from 'lucide-react';

import { cn } from '@/lib/utils';

import type { MultiCountryScheduleMode } from '@/features/smswiser-campaigns/components/MultiCountryBulkEdit/hooks';

const OPTIONS: Array<{
  value: MultiCountryScheduleMode;
  label: string;
  description: string;
  icon: LucideIcon;
}> = [
  {
    value: 'unchanged',
    label: "Keep each drop's schedule",
    description: 'Do not change scheduled send times.',
    icon: CalendarOff,
  },
  {
    value: 'apply',
    label: 'Set one schedule for all',
    description: 'Pick a date and time applied to every draft in this edit.',
    icon: CalendarClock,
  },
  {
    value: 'clear',
    label: 'Clear schedule on all',
    description: 'Remove scheduled send times from every draft in this edit.',
    icon: CalendarX,
  },
];

interface Props {
  value: MultiCountryScheduleMode;
  onChange: (m: MultiCountryScheduleMode) => void;
  disabled?: boolean;
}

export function ScheduleModeRadios({ value, onChange, disabled }: Props) {
  return (
    <fieldset className="space-y-2" disabled={disabled}>
      <legend className="mb-1 text-xs font-medium text-muted-foreground">Scheduled send</legend>
      <div className="space-y-1.5">
        {OPTIONS.map((opt) => {
          const Icon = opt.icon;
          const active = value === opt.value;
          return (
            <label
              key={opt.value}
              className={cn(
                'flex cursor-pointer items-start gap-2.5 rounded-md border px-3 py-2 transition-colors',
                active ? 'border-primary/50 bg-primary/5' : 'border-border/60 hover:bg-muted/30',
                disabled && 'cursor-not-allowed opacity-50',
              )}
            >
              <input
                type="radio"
                name="mc-bulk-schedule-mode"
                className="mt-2 h-3.5 w-3.5 shrink-0 accent-primary"
                checked={active}
                onChange={() => onChange(opt.value)}
              />
              <span
                className={cn(
                  'mt-1.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-border/50 bg-muted/40',
                  active && 'border-primary/30 bg-primary/10 text-primary',
                )}
                aria-hidden
              >
                <Icon className={cn('h-4 w-4', active ? 'text-primary' : 'text-muted-foreground')} />
              </span>
              <span className="min-w-0 flex-1 pt-0.5">
                <span className="block text-xs font-medium text-foreground">{opt.label}</span>
                <span className="mt-0.5 block text-[11px] leading-snug text-muted-foreground">{opt.description}</span>
              </span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}
